import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import MaturaFunctionGraph from "../../../../components/GraphPlotter/MaturaFunctionGraph";
import IntervalInput from "../../../../components/inputs/IntervalInput";
import { selectExtendedVariant } from "../../../extendedVariantSelection";
import { formatLinear, getIntervalRaw, parseSingleInterval } from "./helpers/MaturaPiecewiseFunctionUtils";

// Three continuous pieces: an attained maximum on a plateau, followed by a V.
// Both outer endpoints are excluded. Every configuration has two interior zeros.
export const configurations = [];
for (const width of [2, 3, 4]) for (const join of [-3, -2, -1, 0]) {
  for (const length of [3, 4, 5]) for (const slope of [1, 2]) {
    for (let heightUnits = 1; heightUnits < length; heightUnits += 1) {
      configurations.push({ width, join, length, slope, heightUnits });
    }
  }
}
const original = { width: 2, join: -3, length: 4, slope: 1, heightUnits: 1 };
export function buildData({ width, join, length, slope, heightUnits }) {
  const left = join - width;
  const vertex = join + length;
  const right = vertex + length;
  const height = slope * heightUnits;
  const minimum = height - slope * length;
  return { left, join, vertex, right, height, minimum, slope,
    b1: height + slope * join, b2: minimum - slope * vertex, maxPoints: 3,
    range: { left: minimum, right: height, leftClosed: true, rightClosed: true },
    negative: { left: join + heightUnits, right: right - heightUnits, leftClosed: false, rightClosed: false },
    maximum: { left, right: join, leftClosed: false, rightClosed: true } };
}
export function generateTask(options = {}) {
  const selected = options.variant === "original" ? original : selectExtendedVariant(configurations, options);
  return { type: "MaturaPiecewisePlateauIntervalsOpenTask", data: { ...buildData(selected), variantIndex: selected.variantIndex },
    source: { variant: options.variant === "original" ? "original" : "random", exam: "MATURA", session: "2026_SIE",
      taskNumber: "11", basedOn: "MMAP-P0-100-2608-11" } };
}
export function buildGraphModel(data) {
  const { left, join, vertex, right, height, minimum } = data;
  return {
    points: {
      left: { x: left, y: height, markerSymbol: "circle-open", showLabel: false },
      right: { x: right, y: height, markerSymbol: "circle-open", showLabel: false },
    },
    curves: [{ x: [left, join, vertex, right], y: [height, height, minimum, height], width: 5 }],
    xRange: [Math.min(-1, left - 1), Math.max(1, right + 1)],
    yRange: [Math.min(-1, minimum - 1), height + 1],
  };
}
export function renderTask({ data }) {
  return <div>
    <p>Funkcja <InlineMath math="f" /> jest określona następująco:</p>
    <BlockMath math={`f(x)=\\begin{cases}${data.height}&\\text{dla }x\\in(${data.left},${data.join}],\\\\${formatLinear(-data.slope, data.b1)}&\\text{dla }x\\in(${data.join},${data.vertex}],\\\\${formatLinear(data.slope, data.b2)}&\\text{dla }x\\in(${data.vertex},${data.right}).\\end{cases}`} />
    <p>Wykres funkcji przedstawiono poniżej. Uzupełnij zdania odpowiednimi przedziałami.</p>
    <MaturaFunctionGraph {...buildGraphModel(data)} />
    <ol><li>Podaj zbiór wartości funkcji.</li><li>Podaj zbiór wszystkich argumentów, dla których funkcja przyjmuje wartości ujemne.</li><li>Podaj zbiór wszystkich argumentów, dla których funkcja przyjmuje największą wartość.</li></ol>
  </div>;
}
const fields = [["range", "Zbiór wartości"], ["negative", "Wartości ujemne — argumenty"], ["maximum", "Największa wartość — argumenty"]];
export function renderInput(task, answer = {}, onChange, disabled) {
  return <div style={{ display: "grid", gap: 16 }}>{fields.map(([key, label]) => <div key={key}>
    <label>{label} · 1 pkt</label><IntervalInput label={label} value={answer?.[key] || ""} disabled={disabled}
      placeholder="np. (-3; 2], także ułamki i sqrt(...)" onChange={value => onChange({ ...answer, [key]: value })} />
  </div>)}</div>;
}
export function checkAnswer({ data }, answer = {}) {
  const parts = Object.fromEntries(fields.map(([key]) => {
    const actual = parseSingleInterval(getIntervalRaw(answer, key));
    const expected = data[key];
    return [key, Boolean(actual && Math.abs(actual.left - expected.left) < 1e-9 && Math.abs(actual.right - expected.right) < 1e-9
      && actual.leftClosed === expected.leftClosed && actual.rightClosed === expected.rightClosed)];
  }));
  const score = Object.values(parts).filter(Boolean).length;
  return { correct: score === 3, isCorrect: score === 3, score, maxScore: 3, parts };
}
export const intervalLatex = value => `${value.leftClosed ? "[" : "("}${value.left},${value.right}${value.rightClosed ? "]" : ")"}`;
export function renderAnswer({ data }) { return <>{fields.map(([key, label]) => <p key={key}>{label}: <InlineMath math={intervalLatex(data[key])} /></p>)}</>; }
export function renderExplanation(task) {
  return <><p>Minimum jest osiągane w wierzchołku, a maksimum na całym odcinku stałym. Dlatego oba końce zbioru wartości są domknięte.</p>
    <p>Wartości ujemne odczytujemy między dwoma miejscami zerowymi. Same miejsca zerowe wykluczamy, bo szukamy wartości ściśle ujemnych.</p>
    <p>Przy maksimum uwzględniamy cały odcinek stały: jego lewy koniec jest wyłączony, prawy należy do dziedziny. Prawy skrajny punkt wykresu jest pusty, więc nie dodaje argumentu.</p>
    {renderAnswer(task)}<p>CKE: <InlineMath math="1"/> punkt za każde poprawnie uzupełnione zdanie, łącznie <InlineMath math="3"/> punkty. Rodzaj nawiasów musi być jednoznaczny.</p></>;
}
export function renderUserAnswer(answer = {}) { return <>{fields.map(([key, label]) => <p key={key}>{label}: {getIntervalRaw(answer, key) || "Brak"}</p>)}</>; }
